import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { connectToChild } from 'penpal'
import { openLink } from '~/helpers'
import Loading from '~/components/Loading'
import { webrtc } from '~/providers/MfdProvider'
import { useEDDataBridge } from '~/hooks/useEDDataBridge'
import {useSelector} from "react-redux";

const ShardIframe = () => {
  const iframeRef = useRef()
  const router = useRouter()
  const routerRef = useRef(router)
  routerRef.current = router
  const { shardId, sourceShardId } = router.query
  const [loading, setLoading] = useState(true)
  const connection = useRef(null)
  const token = useSelector(state => {
    return state.authenticationReducer.token
  })
  const showIframe = router.route === '/shardlibrary/[collectionId]/[shardId]' && token

  useEDDataBridge(connection, token)

  const iframeSource = useMemo(() => {
    let baseURL = `${process.env.NEXT_PUBLIC_FORGE_URL}/render.html`

    const urlSearchParams = new URLSearchParams({
      authToken: token
    })

    if (shardId) urlSearchParams.set('shardId', shardId)

    if (sourceShardId) urlSearchParams.set('sourceShardId', sourceShardId)

    return `${baseURL}?${urlSearchParams}`

    // We don't pass shardId and sourceShardId in the deps, because we want to do this only on very first load
  }, [token])

  useEffect(() => {
    if (!token) return

    try {
      connection.current = connectToChild({
        iframe: iframeRef.current,
        methods: {
          clientMounted: (isMounted) => setLoading(!isMounted),
          goToForge: (where) => {
            const collectionId = routerRef.current.query.collectionId || 0
            let path = `/shardlibrary/${collectionId}`

            // I put all the cases because it's more descriptive than an if statement
            switch (where) {
            case 'collection_shard_list': {
              path = '/shardlibrary'
              break;
            }

            case 'collection': {
              path = `/shardlibrary/${collectionId}`
              break;
            }

            default: {
              path = `/shardlibrary/${collectionId}`
              break;
            }
            }

            router.push(path)
          },
          goToURL: (url) => openLink(url),
          rtcSend: (data) => {
            webrtc?.send?.(data)
          },
          rtcSendWhenConnected: (data) => {
            webrtc?.sendWhenConnected?.(data)
          },
          changeRoute: ({
            collectionId,
            shardId,
            sourceShardId
          }) => {
            if (routerRef.current) {
              let path = `/shardlibrary/${collectionId}/${shardId}`

              if (sourceShardId) {
                path += `?sourceShardId=${sourceShardId}`
              }

              routerRef.current.push(path)
            }
          }
        }
      })
    } catch (error) {
      console.warn(error);
    }

    return () => {
      try {
        connection.current?.destroy?.()
      } catch (error) {
        console.warn(error);
      }
    }

  }, [token])

  useEffect(() => {
    const timeout = shardId && setTimeout(() => setLoading(false), 2500)

    return () => {
      clearTimeout(timeout)
    }
  }, [shardId])

  useEffect(() => {
    const changeRoute = async (retries = 3) => {
      if (connection.current && showIframe) {
        try {
          const child = await connection.current.promise

          await child.changeRoute({
            token,
            shardId,
            sourceShardId
          })
        } catch (error) {
          console.error(error)

          if (retries > 0) {
            requestAnimationFrame(() => {
              changeRoute(--retries)
            })
          }
        }
      }
    }

    changeRoute()
  }, [connection.current, showIframe, shardId, sourceShardId, token])

  return (
    <>
      {(showIframe && loading) && <Loading fixed message="Loading shard..." />}
      {token && (
        <iframe
          ref={iframeRef}
          css={`
           display: ${showIframe ? 'block' : 'none'};
            width: 100vw;
           height: 100vh;
          `}
          src={iframeSource}
        />
      )}
    </>
  )
}

const ShardIframeComponent = () => {
  return (
    // <Game allowDeviceRotation withLoading={false}>
    <ShardIframe />
    // </Game>
  )
}

export default ShardIframeComponent
